import { FC } from 'react';
import { motion } from 'framer-motion';
import { useThemeMode } from '@/hooks/useThemeMode';
import { useNavigate } from 'react-router-dom';
import TurboWordmark from './TurboWordmark';
import { useNavigation } from '@/context/NavigationContext';

interface HeaderProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
  animate?: boolean;
  forceLight?: boolean;
}

const Header: FC<HeaderProps> = ({ 
  onMenuClick, 
  showMenuButton = true,
  animate = true,
  forceLight = false 
}) => {
  const { isDark, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const { menuOpen } = useNavigation();
  
  // Use light colors when forceLight is true
  const textColor = forceLight ? 'text-zinc-100' : 'text-zinc-900 dark:text-zinc-100';
  const buttonBg = forceLight ? 'bg-zinc-800' : 'bg-zinc-100 dark:bg-zinc-800';
  const buttonHoverBg = forceLight ? 'hover:bg-zinc-700' : 'hover:bg-zinc-200 dark:hover:bg-zinc-700';
  const iconColor = forceLight ? 'text-zinc-400' : 'text-zinc-600 dark:text-zinc-400';

  const handleNavigation = (href: string) => {
    navigate(href);
  };

  const handleOpenContact = () => {
    if (typeof window !== 'undefined') {
      // @ts-expect-error custom drawer event dispatched globally
      window.dispatchEvent(new Event('open-contact'));
    } else {
      navigate('/contact');
    }
  };

  const animationProps = animate ? {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.5, ease: 'easeOut' }
  } : {};

  return (
    <motion.div
      data-role="site-header"
      {...animationProps}
      animate={{ 
        opacity: menuOpen ? 0 : (animate ? 1 : 1)
      }}
      transition={{ 
        duration: 0.3,
        ease: 'easeOut'
      }}
      className="fixed top-0 left-0 right-0 z-50 h-20 flex items-center justify-between px-8"
    >
      {/* Left: Menu button */}
      {showMenuButton ? (
        <button
          onClick={onMenuClick}
          className={`px-4 py-1.5 ${buttonBg} rounded-full text-sm font-medium ${textColor} ${buttonHoverBg} transition-colors`}
        >
          Menu
        </button>
      ) : (
        <div className="w-[60px]" /> 
      )}

      {/* Center: Logo */}
      <button
        type="button"
        onClick={() => handleNavigation('/')}
        className="group absolute left-1/2 -translate-x-1/2 flex items-center justify-center focus:outline-none"
        aria-label="Go to landing page"
      >
        <TurboWordmark className={`h-5 w-auto ${textColor} transition-opacity group-hover:opacity-80`} />
      </button>

      {/* Right: Ticker and buttons */}
      <div className="flex items-center gap-4">
        {/* Ticker */}
        <div className="flex items-center gap-2">
          <span className={`text-base ${iconColor}`}>TRBO</span>
          <span className={`text-base ${textColor}`}>$24.50</span>
          <span className="text-sm text-green-500">+5.2%</span>
        </div>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className={`w-10 h-10 rounded-full ${buttonBg} flex items-center justify-center ${buttonHoverBg} transition-colors`}
          aria-label="Toggle theme"
        >
          {isDark ? (
            <svg className={`w-5 h-5 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className={`w-5 h-5 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        {/* Contact button */}
        <button
          onClick={handleOpenContact}
          className={`w-10 h-10 rounded-full ${buttonBg} ${iconColor} flex items-center justify-center ${buttonHoverBg} transition-colors font-medium text-sm`}
          aria-label="Contact"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </button>
      </div>
    </motion.div>
  );
};

export default Header;
